import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Select, Checkbox, Card, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { userRequests } from '../../requests';
import UserForm from './UserForm.scss';

const userTypeOptions = [
  { label: "Administrador", value: "admin" },
  { label: "Usuário", value: "user" },
];

const additionalRolesOptions = [
  { label: "Gerenciar disparos", value: 3 },
  { label: "Gerenciar Tyra", value: 4 },
  { label: "Gerenciar Market Stories", value: 5 },
  { label: "Gerenciar despesas", value: 6 },
];

const { Option } = Select;

const UserFormPage = () => {
  const [form] = Form.useForm();
  const { id } = useParams(); // if id exists, edit mode; if not, register mode
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isEditMode = !!id;
  const [userType, setUserType] = useState(null);

  const getUserType = (roleIds) => {
    if (roleIds.includes(1)) {
      return 'admin';
    }
    if (roleIds.includes(2)) {
      return 'user';
    }
    return null;
  }

  // When in edit mode, fetch user data and populate the form
  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      userRequests.getById(id)
        .then((response) => {
          const userData = response.data;
          // Populate the form; note: password is not retrieved
          const newUserType = getUserType(userData.roleIds);
          setUserType(newUserType);
          form.setFieldsValue({
            email: userData.email,
            fullName: userData.fullName,
            userType: newUserType, // expects "admin" or "user"
            additionalRoles: userData.roleIds.filter(v => v != 1 && v != 2),
          });
        })
        .catch((error) => {
          message.error('Error fetching user data');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, isEditMode, form]);

  // When userType changes, if "admin", clear additional roles
  const handleUserTypeChange = (value) => {
    setUserType(value);
    if (value === 'admin') {
      form.setFieldsValue({ additionalRoles: [] });
    }
  };

  const onFinish = (values) => {
    setLoading(true);
    // For Admin, always assign ROLE_ADMIN (id: 1) and ROLE_USER (id: 2)
    // For User, assign ROLE_USER (id: 2) plus any additional roles selected
    let roleIds = [];
    if (values.userType === 'admin') {
      roleIds = [1];
    } else {
      roleIds = [2];
      if (values.additionalRoles && values.additionalRoles.length > 0) {
        roleIds = roleIds.concat(values.additionalRoles);
      }
    }
    const payload = {
      email: values.email,
      fullName: values.fullName,
      password: values.password, // in edit mode, if left blank, the backend should maintain the current password
      roleIds,
    };

    const request = isEditMode ? userRequests.update(payload, id) : userRequests.create(payload);
    request
      .then((response) => {
        message.success(isEditMode ? 'Usuário atualizado com sucesso' : 'Usuário registrado com sucesso');
        navigate('/usuarios/listar');
      })
      .catch((error) => {
        message.error('Error saving user data');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="UserForm">
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Email é obrigatório' },
            { type: 'email', message: 'Email inválido' },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="fullName"
          label="Nome completo"
          rules={[{ required: true, message: 'Nome completo é obrigatório' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="password"
          label="Senha"
          rules={[{ required: !isEditMode, message: 'Senha é obrigatória' }]}
          hasFeedback
        >
          <Input.Password />
        </Form.Item>

        <Form.Item
          name="userType"
          label="Tipo de usuário"
          rules={[{ required: true, message: 'Selecione o tipo de usuário' }]}
        >
          <Select onChange={handleUserTypeChange}>
            {userTypeOptions.map(opt => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {userType === 'user' && (
          <Form.Item
            name="additionalRoles"
            label="Permissões"
          >
            <Checkbox.Group options={additionalRolesOptions} />
          </Form.Item>
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            {isEditMode ? 'Atualizar usuário' : 'Registrar usuário'}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default UserFormPage;
